import * as Sentry from "@sentry/react"
import Pusher, { Channel, PresenceChannel } from "pusher-js"

import Config from "@/config"
import { authenticateCustomerPusher } from "@/services/firebase"
import { auth } from "@/services/firebase/auth"

// How many consecutive WebSocketErrors (without an intervening `connected`) before
// we escalate from breadcrumb to captureException. Three blips without reconnect
// is a sustained outage worth surfacing in Sentry.
const WEBSOCKET_ERROR_ESCALATION_THRESHOLD = 3

// Note: This service always uses trackingToken-based auth with Firebase Functions.
// The app does not use a separate API_URL for Pusher authentication.

// Reconnection configuration
const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000]
const MAX_RECONNECT_ATTEMPTS = 5

// 1001/1005/1006 = WebSocket transient close (RFC 6455); 4200-4299 = Pusher transient/reconnect codes.
// 4000-4099 (fatal/misconfig) and 4100-4199 (server-side transient) DO get captured.
const TRANSIENT_CONNECTION_CODES = new Set([1001, 1005, 1006])

// Event types - matching backend
export enum PusherEvents {
  // Legacy events (for driver app)
  DRIVER_LOCATION_UPDATE = "driver-location",
  ORDER_STATUS_UPDATE = "order-status",
  ETA_UPDATE = "eta-update",
  ROUTE_UPDATE = "route-update",

  // Customer events (from integration brief)
  LOCATION_UPDATE = "location-update",
  DRIVER_STARTED = "driver-started",
  DRIVER_ARRIVED = "driver-arrived",
  DRIVER_CANCELLED = "driver-cancelled",
  STAGE_UPDATE = "stage-update",
  ORDER_CREATED = "order-created",
  NUMBER_PLATE_UPDATE = "number-plate-update",
  // PLA-25: realised pickup moment - fired by markRealisedPickup after the
  // HubSpot write succeeds. Broadcast on both presence-customer-order-{orderId}
  // and tracking-{trackingToken} channels.
  PICKUP_REALIZED = "pickup-realized",
  // PLA-111: fired when an order's inspection-report PDF (berichtPdfStatus)
  // transitions into "available". Broadcast on presence-customer-order-{orderId}
  // — both /track (B2C) and /fleethub (B2B) subscribe to the same channel,
  // so one broadcast wakes both surfaces. Idempotent: the backend predicate
  // guarantees exactly-once-per-order under normal sync; clients should still
  // be safe against duplicate deliveries.
  REPORT_AVAILABLE = "report-available",
}

// Event payload interfaces
export interface DriverLocationUpdate {
  driverId: string
  location: {
    latitude: number
    longitude: number
    timestamp: number
    heading?: number
    speed?: number
  }
  eta?: number // seconds
  routePolyline?: string
}

// Customer location update (from backend)
export interface LocationUpdate {
  orderId: string
  latitude: number
  longitude: number
  timestamp: number
  isReturn: boolean // true if returning, false if picking up
}

// Customer driver status update (from backend)
export interface DriverStatusUpdate {
  orderId: string
  status: "started" | "arrived" | "cancelled"
  isReturn: boolean
  timestamp: number
}

// Stage update event (from backend)
export interface StageUpdate {
  orderId: string
  newStage: string
  previousStage?: string
  timestamp: number
  isReturn?: boolean
}

// Order created event (from backend)
export interface OrderCreatedUpdate {
  orderId: string
  status: "ready"
  timestamp: number
}

// Number plate update event (from backend)
export interface NumberPlateUpdate {
  orderId: string
  numberPlate: string
  timestamp: number
}

// Realised pickup event (PLA-25 - fired after a successful markRealisedPickup
// HubSpot write). Payload carries the moment the PA physically took possession
// of the customer vehicle at the customer address. HBS-only by construction.
// realisedPickupAt is epoch ms on the wire (the same value the QCAI client
// stamped at Submit-tap and PATCHed to HubSpot).
export interface RealisedPickupUpdate {
  orderId: string
  realisedPickupAt: number
}

// PLA-111: backend fires this exactly once per order when berichtPdfStatus
// transitions into "available". The payload carries no PDF URL — the client
// already knows how to fetch via the existing download callable; this event
// just flips the local `hasPdfReport` flag so the download button enables.
export interface ReportAvailableUpdate {
  orderId: string
  timestamp: number
}

export interface StatusUpdate {
  orderId: string
  status: number
  timestamp: number
}

export interface ETAUpdate {
  orderId: string
  eta: Date
  timestamp: number
}

// LiveETA update event (from backend GPS-based computation)
export interface LiveETAUpdate {
  orderId: string
  // ISO timestamp — legacy field, kept for back-compat with any handler
  // that still reads it. Optional because the backend live_return_eta
  // feature flag strips BOTH this and projectedCompletionAtLive from the
  // payload when disabled (they carry the same value; leaving `eta` in
  // would be a kill-switch bypass).
  eta?: string
  // Canonical projected-completion ISO. Same value as `eta` when present.
  // Clients should prefer this name. Optional under the same flag.
  // NEVER synthesise from `eta` as a fallback — that defeats the
  // server-side kill switch.
  projectedCompletionAtLive?: string
  // ADR-0012 §5: projected PA door-arrival ISO. Optional and omitted from the
  // payload when the `live_pickup_eta` flag is off (kill-switch parity with
  // projectedCompletionAtLive). Never `null` on the wire — the backend writes
  // explicit `null` only to the mirror doc; the Pusher payload uses absence.
  projectedPickupAtLive?: string
  // Remaining minutes for the active leg. Optional because pickup-only
  // fan-outs (heartbeat tick during `picking_up`, or any other writer that
  // refines `projectedPickupAtLive` without touching the return-leg minutes)
  // emit `eta-update` payloads without it. Handlers must gracefully accept
  // pickup-only payloads — never early-return on this alone when any of the
  // projection fields are present.
  liveMinutes?: number
  phase: string // driving_to_customer | driving_to_hub | returning_to_customer | pickup-<source>
  timestamp: number
}

export interface RouteUpdate {
  orderId: string
  route: {
    polyline: string
    distance: number
    duration: number
  }
  timestamp: number
}

export interface PusherEventHandlers {
  onDriverLocationUpdate: (data: DriverLocationUpdate) => void
  onStatusUpdate: (data: StatusUpdate) => void
  onETAUpdate: (data: ETAUpdate) => void
  onRouteUpdate: (data: RouteUpdate) => void
}

export interface CustomerPusherEventHandlers {
  onLocationUpdate: (data: LocationUpdate) => void
  onDriverStarted: (data: DriverStatusUpdate) => void
  onDriverArrived: (data: DriverStatusUpdate) => void
  onDriverCancelled: (data: DriverStatusUpdate) => void
  onStageUpdate?: (data: StageUpdate) => void
  onOrderCreated?: (data: OrderCreatedUpdate) => void
  onNumberPlateUpdate?: (data: NumberPlateUpdate) => void
  onETAUpdate?: (data: LiveETAUpdate) => void
  onPickupRealized?: (data: RealisedPickupUpdate) => void
  onReportAvailable?: (data: ReportAvailableUpdate) => void
}

export class PusherService {
  private pusher: Pusher
  private orderChannel: Channel | PresenceChannel | null = null
  private isConnected: boolean = false
  private trackingToken: string | null = null
  private currentOrderId: string | null = null
  private reconnectAttempts = 0
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  private shouldReconnect: boolean = true
  // Callback that re-runs the full channel subscription (name + all event bindings).
  // Set by subscribeToOrder / subscribeToCustomerOrder; cleared on unsubscribe.
  // Used by the TIMEOUT_ERROR one-shot retry to avoid losing event bindings.
  private _resubscribeCb: (() => void) | null = null
  // True while _retryAfterTimeout is executing its resubscription. subscribe methods
  // skip resetting _authRetryUsed when this is true, so the replacement channel
  // inherits the "already retried" state and won't attempt a second retry.
  private _retryCallInProgress = false
  // True after one TIMEOUT_ERROR retry has been dispatched for the current user-
  // initiated subscription. Reset to false only by a new user call (not retry path).
  // Prevents the replacement channel from retrying again if it also times out.
  private _authRetryUsed = false
  // Counts WebSocketErrors since the last successful `connected` event.
  // Resets to 0 on connected. Escalates to Sentry.captureException when
  // it reaches WEBSOCKET_ERROR_ESCALATION_THRESHOLD.
  private consecutiveWebSocketErrors = 0

  constructor(trackingToken?: string) {
    this.trackingToken = trackingToken || null

    // Build options conditionally. pusher-js does `'params' in options.auth`
    // without a null-check, so passing `auth: undefined` (vs omitting the key)
    // throws "Cannot use 'in' operator to search for 'params' in undefined".
    // We OMIT auth/authorizer entirely when there's no trackingToken (public
    // channel only — used by FleetHub's pending-order pre-subscription).
    const options: any = {
      cluster: Config.PUSHER_CLUSTER || "eu",
      forceTLS: true,
    }

    if (trackingToken) {
      options.auth = {
        params: {
          orderId: "", // Will be set during subscription
          trackingToken: trackingToken,
        },
      }
      options.authorizer = (channel: any) => {
        return {
          authorize: async (socketId: string, callback: any) => {
            try {
              if (!this.currentOrderId || !this.trackingToken) {
                callback(new Error("Missing orderId or trackingToken"), null)
                return
              }

              // Authenticate via Firebase Cloud Function
              const result = await authenticateCustomerPusher({
                socketId,
                channelName: channel.name,
                orderId: this.currentOrderId,
                trackingToken: this.trackingToken,
              })

              callback(null, {
                auth: result.auth,
                channel_data: result.channel_data,
              })
            } catch (_error) {
              Sentry.captureException(_error, {
                tags: { flow: "pusher", operation: "auth" },
              })
              callback(_error, null)
            }
          },
        }
      }
    }

    this.pusher = new Pusher(Config.PUSHER_APP_KEY || "demo-key", options)

    // Connection state listeners
    this.pusher.connection.bind("connected", () => {
      this.isConnected = true
      this.reconnectAttempts = 0
      this.shouldReconnect = true
      // A successful reconnect clears any accumulated WebSocketError blip count.
      this.consecutiveWebSocketErrors = 0
    })

    this.pusher.connection.bind("disconnected", () => {
      this.isConnected = false
      this.scheduleReconnect()
    })

    this.pusher.connection.bind("error", (_error: any) => {
      // pusher-js emits two distinct error shapes on connection.bind("error"):
      //
      // 1. { type: "WebSocketError", error: <DOM ErrorEvent> }
      //    Fired by socket.onerror — the WebSocket spec does NOT expose the close
      //    code here (it only appears on socket.onclose). So data.code is always
      //    undefined. These are transient mobile-network blips and must NOT be
      //    captured individually — doing so produced ~250 Sentry events/day
      //    (CARASSIST-WEB-1F). Instead, record a breadcrumb and escalate only
      //    if the threshold of consecutive blips is reached without a reconnect.
      //
      // 2. { type: "PusherError", data: { code: number, message?: string } }
      //    Fired for application-level errors (4xxx range). Apply the existing
      //    code-band filter: transient 4200-4299 are dropped; fatal/misconfig
      //    4000-4099 and server-side transient 4100-4199 are captured.
      //
      // Reconnect note: pusher-js always emits `disconnected` after `error`, so
      // the existing bind("disconnected") → scheduleReconnect() path handles
      // reconnection for both error types. No second reconnect call is needed here.

      const type: string = _error?.type ?? "unknown"

      if (type === "WebSocketError") {
        // pusher-js can nest a PusherError inside the WebSocketError when the
        // handshake/close path builds the error callback (ConnectionManager
        // .buildErrorCallbacks). In that case the shape is:
        //   { type: "WebSocketError", error: { type: "PusherError", data: { code } } }
        // A nested fatal code (4000-4099, i.e. NOT in the transient set) must be
        // captured immediately — it signals bad app key / SSL-only / not-subscribed
        // and would be invisible if only treated as a transport blip.
        const nestedCode: number | undefined = _error?.error?.data?.code
        const nestedIsTransient =
          nestedCode !== undefined &&
          (TRANSIENT_CONNECTION_CODES.has(nestedCode) || (nestedCode >= 4200 && nestedCode <= 4299))

        if (nestedCode !== undefined && !nestedIsTransient) {
          // Nested fatal PusherError — same capture path as a top-level PusherError.
          Sentry.captureException(
            new Error(`Pusher connection error: WebSocketError[PusherError] code=${nestedCode}`),
            {
              tags: {
                flow: "pusher",
                operation: "connection-error",
                closeCode: String(nestedCode),
                errorType: "WebSocketError",
              },
              extra: { payload: _error },
            },
          )
          return
        }

        // No nested fatal code — treat as a transient transport blip.
        // Breadcrumb-first: record every blip without creating a Sentry issue.
        this.consecutiveWebSocketErrors += 1
        Sentry.addBreadcrumb({
          category: "pusher",
          message: `WebSocketError blip (${this.consecutiveWebSocketErrors} consecutive)`,
          level: "warning",
          data: { errorMessage: _error?.error?.message ?? "unknown" },
        })

        // Escalate to captureException only when we see a sustained run of
        // blips without an intervening `connected` — that's a real outage.
        if (this.consecutiveWebSocketErrors >= WEBSOCKET_ERROR_ESCALATION_THRESHOLD) {
          Sentry.captureException(
            new Error(
              `Pusher sustained WebSocketError: ${this.consecutiveWebSocketErrors} consecutive errors without reconnect`,
            ),
            {
              tags: {
                flow: "pusher",
                operation: "connection-error",
                errorType: "WebSocketError",
                consecutiveErrors: String(this.consecutiveWebSocketErrors),
              },
              extra: { lastPayload: _error },
            },
          )
          // Reset after escalation so we don't fire again until the count
          // accumulates past the threshold a second time.
          this.consecutiveWebSocketErrors = 0
        }
        return
      }

      // PusherError (or unknown type) — apply close-code band filter.
      const code: number | undefined = _error?.data?.code
      const isTransientNetwork =
        code !== undefined &&
        (TRANSIENT_CONNECTION_CODES.has(code) || (code >= 4200 && code <= 4299))

      if (!isTransientNetwork) {
        // Capture fatal/misconfig (4000-4099), server-side transient (4100-4199),
        // unknown/undefined codes (fail-open), and non-numeric shapes.
        Sentry.captureException(
          new Error(`Pusher connection error: ${type} code=${code ?? "unknown"}`),
          {
            tags: {
              flow: "pusher",
              operation: "connection-error",
              closeCode: String(code ?? "unknown"),
            },
            extra: { payload: _error },
          },
        )
      }
    })
  }

  private scheduleReconnect() {
    // Don't reconnect if intentionally disconnected
    if (!this.shouldReconnect) {
      return
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
    }

    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      return
    }

    const delay =
      RECONNECT_DELAYS[this.reconnectAttempts] || RECONNECT_DELAYS[RECONNECT_DELAYS.length - 1]

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++
      this.pusher.connect()
    }, delay)
  }

  /**
   * Attempt a one-shot resubscription after a TIMEOUT_ERROR on channel auth.
   *
   * Steps:
   * 1. Force-refresh the Firebase ID token so a logged-in user's token is fresh
   *    (no-op when currentUser is null — B2C tracking users have no session).
   * 2. Call the stored _resubscribeCb which unsubscribes and re-runs the full
   *    subscription (channel name + all event bindings).
   *
   * Returns true if the retry was attempted, false if no resubscription callback
   * was registered (e.g. public channels that skip auth).
   */
  private async _retryAfterTimeout(): Promise<boolean> {
    const cb = this._resubscribeCb
    if (!cb) return false

    try {
      await auth.currentUser?.getIdToken(/* forceRefresh */ true)
    } catch {
      // Token refresh is best-effort; proceed with retry regardless
    }

    // Bail if the subscription was torn down or replaced during the token-refresh
    // await. unsubscribe() nulls _resubscribeCb (and destroys the channel) and a new
    // subscribeTo* call swaps in a different callback; invoking the stale `cb` here
    // would resurrect a dead/old subscription after teardown. (Codex P2, PLA-189.)
    if (this._resubscribeCb !== cb) return false

    // Mark that a retry has been used for this subscription lifecycle.
    // _retryCallInProgress prevents the subscribe methods from resetting _authRetryUsed,
    // so the replacement channel inherits the "already retried" state.
    this._authRetryUsed = true
    this._retryCallInProgress = true
    try {
      cb()
    } finally {
      this._retryCallInProgress = false
    }
    return true
  }

  /**
   * Build a `pusher:subscription_error` handler.
   *
   * Behaviour:
   * - NETWORK_ERROR → suppressed (transient, existing behaviour).
   * - TIMEOUT_ERROR when _authRetryUsed=false (first attempt) → retry once via
   *   _retryAfterTimeout.  _authRetryUsed is set to true before resubscribing so
   *   the replacement channel's handler sees the flag and falls through to capture.
   *   _retryCallInProgress prevents the subscribe methods from resetting the flag
   *   during the internal resubscription.
   * - TIMEOUT_ERROR when _authRetryUsed=true → capture (second-attempt failure).
   * - Any other non-transient error → capture immediately (existing behaviour).
   */
  private _buildSubscriptionErrorHandler() {
    return (error: any): void => {
      const msg = typeof error?.error === "string" ? error.error : ""
      const isNetworkTransient = msg.includes("NETWORK_ERROR")
      const isTimeout = msg.includes("TIMEOUT_ERROR")

      if (isNetworkTransient) return

      if (isTimeout && !this._authRetryUsed) {
        // One-shot retry — fire and forget; second-attempt errors fall through to capture
        this._retryAfterTimeout().catch(() => {
          // Sync throw from _resubscribeCb — should not happen in practice
          Sentry.captureException(
            new Error(`Pusher subscription error: ${JSON.stringify(error)}`),
            { tags: { flow: "pusher", operation: "subscription-error" } },
          )
        })
        return
      }

      // Capture: second-attempt failures, non-timeout auth errors, other errors
      Sentry.captureException(new Error(`Pusher subscription error: ${JSON.stringify(error)}`), {
        tags: { flow: "pusher", operation: "subscription-error" },
      })
    }
  }

  // Legacy method for driver app
  subscribeToOrder(orderId: string, handlers: PusherEventHandlers) {
    const channelName = `private-order-${orderId}`

    // Unsubscribe from previous channel if exists
    if (this.orderChannel) {
      this.unsubscribe()
    }

    // Store resubscription callback so TIMEOUT_ERROR retry can rebuild the channel.
    // Reset the auth-retry flag only for fresh user-initiated calls; the internal
    // retry path sets _retryCallInProgress=true so we preserve the "already retried"
    // state on the replacement channel.
    this._resubscribeCb = () => this.subscribeToOrder(orderId, handlers)
    if (!this._retryCallInProgress) this._authRetryUsed = false

    // Subscribe to private channel for this order
    this.orderChannel = this.pusher.subscribe(channelName)

    // Bind event handlers
    this.orderChannel.bind(PusherEvents.DRIVER_LOCATION_UPDATE, (data: DriverLocationUpdate) => {
      handlers.onDriverLocationUpdate(data)
    })

    this.orderChannel.bind(PusherEvents.ORDER_STATUS_UPDATE, (data: StatusUpdate) => {
      handlers.onStatusUpdate(data)
    })

    this.orderChannel.bind(PusherEvents.ETA_UPDATE, (data: ETAUpdate) => {
      handlers.onETAUpdate(data)
    })

    this.orderChannel.bind(PusherEvents.ROUTE_UPDATE, (data: RouteUpdate) => {
      handlers.onRouteUpdate(data)
    })

    // Handle subscription errors with TIMEOUT_ERROR one-shot retry
    this.orderChannel.bind("pusher:subscription_error", this._buildSubscriptionErrorHandler())

    this.orderChannel.bind("pusher:subscription_succeeded", () => {
      // Successfully subscribed to order channel
    })
  }

  // New method for customer tracking
  subscribeToCustomerOrder(orderId: string, handlers: CustomerPusherEventHandlers) {
    const channelName = `presence-customer-order-${orderId}`
    this.currentOrderId = orderId

    // Unsubscribe from previous channel if exists
    if (this.orderChannel) {
      this.unsubscribe()
    }

    // Store resubscription callback so TIMEOUT_ERROR retry can rebuild the channel.
    // Reset the auth-retry flag only for fresh user-initiated calls; the internal
    // retry path sets _retryCallInProgress=true so we preserve the "already retried"
    // state on the replacement channel.
    this._resubscribeCb = () => this.subscribeToCustomerOrder(orderId, handlers)
    if (!this._retryCallInProgress) this._authRetryUsed = false

    // Subscribe to presence channel for customer tracking
    this.orderChannel = this.pusher.subscribe(channelName) as PresenceChannel

    // Bind customer event handlers
    this.orderChannel.bind(PusherEvents.LOCATION_UPDATE, (data: LocationUpdate) => {
      // Customer location update received
      handlers.onLocationUpdate(data)
    })

    this.orderChannel.bind(PusherEvents.DRIVER_STARTED, (data: DriverStatusUpdate) => {
      // Driver started
      handlers.onDriverStarted(data)
    })

    this.orderChannel.bind(PusherEvents.DRIVER_ARRIVED, (data: DriverStatusUpdate) => {
      // Driver arrived
      handlers.onDriverArrived(data)
    })

    this.orderChannel.bind(PusherEvents.DRIVER_CANCELLED, (data: DriverStatusUpdate) => {
      // Driver cancelled
      handlers.onDriverCancelled(data)
    })

    // Bind stage update event if handler provided
    if (handlers.onStageUpdate) {
      this.orderChannel.bind(PusherEvents.STAGE_UPDATE, (data: StageUpdate) => {
        // Stage update received
        handlers.onStageUpdate!(data)
      })
    }

    // Bind order created event if handler provided
    if (handlers.onOrderCreated) {
      this.orderChannel.bind(PusherEvents.ORDER_CREATED, (data: OrderCreatedUpdate) => {
        // Order created notification received
        handlers.onOrderCreated!(data)
      })
    }

    // Bind number plate update event if handler provided
    if (handlers.onNumberPlateUpdate) {
      this.orderChannel.bind(PusherEvents.NUMBER_PLATE_UPDATE, (data: NumberPlateUpdate) => {
        // Number plate update received
        handlers.onNumberPlateUpdate!(data)
      })
    }

    // Bind LiveETA update event if handler provided
    if (handlers.onETAUpdate) {
      this.orderChannel.bind(PusherEvents.ETA_UPDATE, (data: LiveETAUpdate) => {
        handlers.onETAUpdate!(data)
      })
    }

    // Bind realised-pickup event if handler provided (PLA-25)
    if (handlers.onPickupRealized) {
      this.orderChannel.bind(PusherEvents.PICKUP_REALIZED, (data: RealisedPickupUpdate) => {
        handlers.onPickupRealized!(data)
      })
    }

    // Bind report-available event if handler provided (PLA-111)
    if (handlers.onReportAvailable) {
      this.orderChannel.bind(PusherEvents.REPORT_AVAILABLE, (data: ReportAvailableUpdate) => {
        handlers.onReportAvailable!(data)
      })
    }

    // Handle subscription errors with TIMEOUT_ERROR one-shot retry
    this.orderChannel.bind("pusher:subscription_error", this._buildSubscriptionErrorHandler())

    this.orderChannel.bind("pusher:subscription_succeeded", () => {
      // Successfully subscribed to customer order channel
    })
  }

  unsubscribe() {
    this._resubscribeCb = null
    if (this.orderChannel) {
      // Unbind all events
      this.orderChannel.unbind_all()

      // Unsubscribe from channel
      this.pusher.unsubscribe(this.orderChannel.name)
      this.orderChannel = null
    }
  }

  disconnect() {
    // Prevent automatic reconnection after intentional disconnect
    this.shouldReconnect = false

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
    this.reconnectAttempts = 0
    this.unsubscribe()
    this.pusher.disconnect()
  }

  getConnectionState() {
    return {
      isConnected: this.isConnected,
      state: this.pusher.connection.state,
    }
  }

  // Reconnect if disconnected
  reconnect() {
    this.shouldReconnect = true
    if (!this.isConnected) {
      this.pusher.connect()
    }
  }

  // Subscribe to a public ticketId-keyed channel for order-created notifications.
  // Used by FleetHub right after createFleetHubOrder returns: the HubSpot ticketId
  // is known but the Firestore sync hasn't run yet (no trackingToken, no order doc).
  // The backend broadcasts ORDER_CREATED on this channel as soon as the sync
  // completes, eliminating the need to poll until the order becomes visible.
  //
  // Expected to be called on a dedicated PusherService instance with no prior
  // subscription. Reuses `this.orderChannel`, so calling this on an instance that
  // already holds a presence-channel subscription would silently drop it.
  subscribeToPendingOrder(orderId: string, onOrderCreated: (data: OrderCreatedUpdate) => void) {
    const channelName = `pending-order-${orderId}`

    // Unsubscribe from previous channel if exists
    if (this.orderChannel) {
      this.unsubscribe()
    }

    // Subscribe to public channel (no auth needed)
    this.orderChannel = this.pusher.subscribe(channelName)

    // Bind order created event
    this.orderChannel.bind(PusherEvents.ORDER_CREATED, (data: OrderCreatedUpdate) => {
      onOrderCreated(data)
    })

    // Handle subscription events
    this.orderChannel.bind("pusher:subscription_error", (error: any) => {
      const msg = typeof error?.error === "string" ? error.error : ""
      const isTransientNetwork = msg.includes("NETWORK_ERROR")
      console.warn("[Pusher] Subscription error:", error)
      if (!isTransientNetwork) {
        Sentry.captureException(new Error(`Pusher subscription error: ${JSON.stringify(error)}`), {
          tags: { flow: "pusher", operation: "subscription-error" },
        })
      }
    })

    this.orderChannel.bind("pusher:subscription_succeeded", () => {
      // Successfully subscribed to pending order channel
    })
  }

  // Subscribe to a public tracking-token channel for order-created notifications
  // This allows listening for order creation before we have the orderId
  subscribeToTrackingToken(
    trackingToken: string,
    onOrderCreated: (data: OrderCreatedUpdate) => void,
  ) {
    const channelName = `tracking-${trackingToken}`

    // Unsubscribe from previous channel if exists
    if (this.orderChannel) {
      this.unsubscribe()
    }

    // Subscribe to public channel (no auth needed)
    this.orderChannel = this.pusher.subscribe(channelName)

    // Bind order created event
    this.orderChannel.bind(PusherEvents.ORDER_CREATED, (data: OrderCreatedUpdate) => {
      onOrderCreated(data)
    })

    // Handle subscription events
    this.orderChannel.bind("pusher:subscription_error", (error: any) => {
      const msg = typeof error?.error === "string" ? error.error : ""
      const isTransientNetwork = msg.includes("NETWORK_ERROR")
      console.warn("[Pusher] Subscription error:", error)
      if (!isTransientNetwork) {
        Sentry.captureException(new Error(`Pusher subscription error: ${JSON.stringify(error)}`), {
          tags: { flow: "pusher", operation: "subscription-error" },
        })
      }
    })

    this.orderChannel.bind("pusher:subscription_succeeded", () => {
      // Successfully subscribed to tracking token channel
    })
  }
}
